import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, setAccessToken, bootstrapSession } from "./api";

interface AuthUser {
  id: string;
  companyId: string;
  role: "owner" | "admin" | "member";
  isPlatformAdmin: boolean;
  email: string;
}
interface Company {
  id: string;
  name: string;
  billingStatus: string;
  hasPaymentMethod: boolean;
}

interface AuthCtx {
  user: AuthUser | null;
  company: Company | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (companyName: string, name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>(null as unknown as AuthCtx);
export const useAuth = () => useContext(Ctx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  async function refreshMe() {
    try {
      const me = await api.get<{ user: AuthUser; company: Company }>("/auth/me");
      setUser(me.user);
      setCompany(me.company);
    } catch {
      setUser(null);
      setCompany(null);
    }
  }

  useEffect(() => {
    (async () => {
      if (await bootstrapSession()) await refreshMe();
      setLoading(false);
    })();
  }, []);

  async function login(email: string, password: string) {
    const res = await api.post<{ accessToken: string; user: AuthUser }>("/auth/login", {
      email,
      password,
    });
    setAccessToken(res.accessToken);
    setUser(res.user);
    await refreshMe();
  }

  async function signup(companyName: string, name: string, email: string, password: string) {
    const res = await api.post<{ accessToken: string; user: AuthUser; company: Company }>(
      "/auth/signup",
      { companyName, name, email, password },
    );
    setAccessToken(res.accessToken);
    setUser(res.user);
    setCompany(res.company);
  }

  async function logout() {
    await api.post("/auth/logout").catch(() => {});
    setAccessToken(null);
    setUser(null);
    setCompany(null);
  }

  return (
    <Ctx.Provider value={{ user, company, loading, login, signup, logout, refreshMe }}>
      {children}
    </Ctx.Provider>
  );
}
